import {
  Assignment,
  AttachMoney,
  Business,
  Calculate,
  ShoppingCart,
} from '@mui/icons-material';
import { Box, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { getSummary } from 'lib/api/publicMarket';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import TableCard from 'lib/designSystem/Card/TableCard';
import BarChart from 'lib/designSystem/Charts/BarChart';
import CentralPieChart from 'lib/designSystem/Charts/CentralPieChart';
import LineChart from 'lib/designSystem/Charts/LineChart';
import StatCard from 'lib/designSystem/Charts/StatCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { BASE_COLORS, CHART_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

type ChartType = 'tenders' | 'orders';
type LineChartType = 'amount' | 'quantity';
type PieChartType = 'status' | 'type';

type SummaryFilters = {
  selectedBuyer: string;
  selectedSupplier: string;
  initialDate: string | null;
  finalDate: string | null;
};

const buildSummaryQuery = ({
  selectedBuyer,
  selectedSupplier,
  initialDate,
  finalDate,
}: SummaryFilters) => {
  const params = new URLSearchParams();
  if (selectedBuyer) params.append('buyerId', selectedBuyer);
  if (selectedSupplier) params.append('supplierId', selectedSupplier);
  if (initialDate) params.append('initialDate', initialDate);
  if (finalDate) params.append('finalDate', finalDate);
  return params.toString();
};

const Summary = () => {
  const dispatch = useDispatch();
  const [selectedBuyer, setSelectedBuyer] = useState<string>('');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');
  const [tenderPieChartType, setTenderPieChartType] =
    useState<PieChartType>('status');
  const [purchaseOrderPieChartType, setPurchaseOrderPieChartType] =
    useState<PieChartType>('status');
  const [tenderChartType, setTenderChartType] =
    useState<LineChartType>('amount');
  const [purchaseOrderChartType, setPurchaseOrderChartType] =
    useState<LineChartType>('amount');
  const [buyerChartType, setBuyerChartType] = useState<ChartType>('tenders');
  const [supplierChartType, setSupplierChartType] =
    useState<ChartType>('tenders');
  const [initialDate, setInitialDate] = useState<string | null>(
    dayjs().subtract(15, 'day').format('YYYY-MM-DD'),
  );
  const [finalDate, setFinalDate] = useState<string | null>(
    dayjs().format('YYYY-MM-DD'),
  );

  const activeFilters = useMemo(
    () => ({
      selectedBuyer,
      selectedSupplier,
      initialDate,
      finalDate,
    }),
    [selectedBuyer, selectedSupplier, initialDate, finalDate],
  );
  const [appliedFilters, setAppliedFilters] =
    useState<SummaryFilters>(activeFilters);
  const query = useMemo(
    () => buildSummaryQuery(appliedFilters),
    [appliedFilters],
  );

  const { data, isPending, refetch } = getSummary(dispatch, query);

  if (isPending) return <CenteredCircularProgress />;

  const colors = Object.values(CHART_COLORS).slice(2);

  const handleSearch = async () => {
    const shouldRefetch = buildSummaryQuery(activeFilters) === query;
    setAppliedFilters(activeFilters);
    if (shouldRefetch) await refetch();
  };

  const filters: Filter[] = [
    {
      type: 'autocomplete',
      label: 'Comprador',
      key: 'buyer',
      value: selectedBuyer,
      setter: setSelectedBuyer,
    },
    {
      type: 'autocomplete',
      label: 'Proveedor',
      key: 'supplier',
      value: selectedSupplier,
      setter: setSelectedSupplier,
    },
    {
      type: 'date',
      label: 'Fecha inicio',
      dateKey: 'startDate',
      value: initialDate,
      setter: setInitialDate,
    },
    {
      type: 'date',
      label: 'Fecha fin',
      dateKey: 'endDate',
      value: finalDate,
      setter: setFinalDate,
    },
  ];

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: -36, right: 0 }}>
        <GlobalSearcher
          filters={filters}
          triggerSearch={handleSearch}
          dispatch={dispatch}
        />
      </Box>

      <Grid container spacing={2} my={1}>
        <Grid item xs={3}>
          <StatCard
            label="Cantidad de proveedores"
            value={data?.statistics.supplierCount || 0}
            valueLabel=""
            color={BASE_COLORS.blue}
            icon={<Business sx={{ fontSize: 24 }} />}
          />
        </Grid>

        <Grid item xs={3}>
          <StatCard
            label="Cantidad de compradores"
            value={data?.statistics.buyerCount || 0}
            valueLabel=""
            color={BASE_COLORS.purple}
            icon={<Business sx={{ fontSize: 24 }} />}
          />
        </Grid>

        <Grid item xs={3}>
          <StatCard
            label="Cantidad de licitaciones"
            value={data?.statistics.tenderCount || 0}
            valueLabel=""
            color={BASE_COLORS.green}
            icon={<Assignment sx={{ fontSize: 24 }} />}
          />
        </Grid>

        <Grid item xs={3}>
          <StatCard
            label="Cantidad de ordenes"
            value={data?.statistics.purchaseOrderCount || 0}
            valueLabel=""
            color={BASE_COLORS.orange}
            icon={<ShoppingCart sx={{ fontSize: 24 }} />}
          />
        </Grid>

        <Grid item xs={8}>
          <TableCard>
            <SubtitleWithTooltip
              title={
                tenderChartType === 'amount'
                  ? 'Monto adjudicado en el tiempo'
                  : 'Cantidad de licitaciones adjudicadas en el tiempo'
              }
              tooltipContent={
                tenderChartType === 'amount'
                  ? 'Evolución del monto adjudicado a lo largo del tiempo'
                  : 'Evolución de la cantidad de licitaciones adjudicadas a lo largo del tiempo'
              }
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={tenderChartType}
                onChange={(value) => setTenderChartType(value as LineChartType)}
                options={[
                  { value: 'amount', label: 'Monto' },
                  { value: 'quantity', label: 'Cantidad' },
                ]}
              />
            </SubtitleWithTooltip>
            <LineChart
              data={
                tenderChartType === 'amount'
                  ? data?.awardedTendersByAmountLineChartData || []
                  : data?.awardedTendersByQuantityLineChartData || []
              }
              colors={colors}
              showArea={false}
              height={480}
            />
          </TableCard>
        </Grid>

        <Grid container item spacing={2} xs={4}>
          <Grid item xs={12}>
            <TableCard>
              <SubtitleWithTooltip
                title="Licitaciones próximas a vencer"
                tooltipContent="Distribución de licitaciones próximas a vencer"
                tooltipPlacement="right"
                variant="compact"
              />
              <CentralPieChart
                data={data?.tendersClosingSoonPieChartData || []}
              />
            </TableCard>
          </Grid>

          <Grid item xs={12}>
            <StatCard
              label="Monto adjudicado"
              value={formatMoney(data?.statistics.adjudicatedAmount || 0)}
              valueLabel="CLP"
              color={BASE_COLORS.blue}
              icon={<AttachMoney sx={{ fontSize: 24 }} />}
            />
          </Grid>
        </Grid>

        <Grid container item spacing={2} xs={4}>
          <Grid item xs={12}>
            <StatCard
              label="Monto transado"
              value={formatMoney(data?.statistics.purchaseOrdersAmount || 0)}
              valueLabel="CLP"
              color={BASE_COLORS.orange}
              icon={<Calculate sx={{ fontSize: 24 }} />}
            />
          </Grid>

          <Grid item xs={12}>
            <TableCard>
              <SubtitleWithTooltip
                title="Ordenes de compra por monto"
                tooltipContent="Distribución de ordenes de compra según su rango de monto"
                tooltipPlacement="right"
                variant="compact"
              />
              <CentralPieChart
                data={data?.purchaseOrdersByAmountRangePieChartData || []}
              />
            </TableCard>
          </Grid>
        </Grid>

        <Grid item xs={8}>
          <TableCard>
            <SubtitleWithTooltip
              title={
                purchaseOrderChartType === 'amount'
                  ? 'Monto de ordenes de compra en el tiempo'
                  : 'Cantidad de ordenes de compra en el tiempo'
              }
              tooltipContent={
                purchaseOrderChartType === 'amount'
                  ? 'Evolución del monto de ordenes de compra a lo largo del tiempo'
                  : 'Evolución de la cantidad de ordenes de compra a lo largo del tiempo'
              }
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={purchaseOrderChartType}
                onChange={(value) =>
                  setPurchaseOrderChartType(value as LineChartType)
                }
                options={[
                  { value: 'amount', label: 'Monto' },
                  { value: 'quantity', label: 'Cantidad' },
                ]}
              />
            </SubtitleWithTooltip>
            <LineChart
              data={
                purchaseOrderChartType === 'amount'
                  ? data?.purchaseOrdersByAmountLineChartData || []
                  : data?.purchaseOrdersByQuantityLineChartData || []
              }
              colors={colors}
              showArea={false}
              height={480}
            />
          </TableCard>
        </Grid>

        <Grid item xs={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Compradores"
              tooltipContent="Top 10 compradores ordenados por monto"
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={buyerChartType}
                onChange={(value) =>
                  setBuyerChartType(value as 'tenders' | 'orders')
                }
                options={[
                  { value: 'tenders', label: 'Licitaciones' },
                  { value: 'orders', label: 'Ordenes' },
                ]}
              />
            </SubtitleWithTooltip>
            <BarChart
              data={
                buyerChartType === 'tenders'
                  ? data?.top10BuyersByAwardedAmount || []
                  : data?.top10BuyersByAmountTraded || []
              }
              direction="horizontal"
              height={350}
              yAxisFormat="auto"
              colors={[
                buyerChartType === 'tenders'
                  ? CHART_COLORS.blue
                  : CHART_COLORS.lightBlue,
              ]}
            />
          </TableCard>
        </Grid>

        <Grid item xs={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Proveedores"
              tooltipContent="Top 10 proveedores ordenados por monto"
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={supplierChartType}
                onChange={(value) =>
                  setSupplierChartType(value as 'tenders' | 'orders')
                }
                options={[
                  { value: 'tenders', label: 'Licitaciones' },
                  { value: 'orders', label: 'Ordenes' },
                ]}
              />
            </SubtitleWithTooltip>
            <BarChart
              data={
                supplierChartType === 'tenders'
                  ? data?.top10SuppliersByAwardedAmount || []
                  : data?.top10SuppliersByAmountTraded || []
              }
              direction="horizontal"
              height={350}
              yAxisFormat="auto"
              colors={[
                supplierChartType === 'tenders'
                  ? CHART_COLORS.purple
                  : CHART_COLORS.pink,
              ]}
            />
          </TableCard>
        </Grid>

        <Grid item xs={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Categorías"
              tooltipContent="Top 10 categorías ordenadas por monto"
              tooltipPlacement="right"
              variant="compact"
            />
            <BarChart
              data={data?.top10CategoriesByAwardedAmount || []}
              direction="horizontal"
              height={350}
              yAxisFormat="auto"
              colors={[CHART_COLORS.green]}
            />
          </TableCard>
        </Grid>

        <Grid item xs={6}>
          <TableCard>
            <SubtitleWithTooltip
              title="Ordenes de compra"
              tooltipContent={
                purchaseOrderPieChartType === 'type'
                  ? 'Distribución de ordenes de compra según su tipo'
                  : 'Distribución de ordenes de compra según su estado'
              }
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={purchaseOrderPieChartType}
                onChange={(value) =>
                  setPurchaseOrderPieChartType(value as PieChartType)
                }
                options={[
                  { value: 'type', label: 'Tipo' },
                  { value: 'status', label: 'Estado' },
                ]}
              />
            </SubtitleWithTooltip>
            <CentralPieChart
              data={
                purchaseOrderPieChartType === 'type'
                  ? data?.purchaseOrderByTypePieChartData || []
                  : data?.purchaseOrderByStatusPieChartData || []
              }
            />
          </TableCard>
        </Grid>

        <Grid item xs={6}>
          <TableCard>
            <SubtitleWithTooltip
              title="Licitaciones"
              tooltipContent={
                tenderPieChartType === 'type'
                  ? 'Distribución de licitaciones según su tipo'
                  : 'Distribución de licitaciones según su estado'
              }
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={tenderPieChartType}
                onChange={(value) =>
                  setTenderPieChartType(value as PieChartType)
                }
                options={[
                  { value: 'status', label: 'Estado' },
                  { value: 'type', label: 'Tipo' },
                ]}
              />
            </SubtitleWithTooltip>
            <CentralPieChart
              data={
                tenderPieChartType === 'status'
                  ? data?.tenderByStatusPieChartData || []
                  : data?.tenderByTypePieChartData || []
              }
            />
          </TableCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Summary;
